/**
 * TanStack Query hooks for `clickdealer-integration-via-api`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	clickdealerIntegrationViaApiQueryKeys,
	createCredentials,
	createOffer,
	getStatus,
	listCredentials,
	listOffers,
	listPostbacks,
	type ClickdealerCredentialsRow,
	type ClickdealerOfferRow,
	type ClickdealerStatusResponse,
	type CreateCredentialsBody,
	type CreateOfferBody,
	type ListCredentialsResponse,
	type ListOffersResponse,
	type ListPostbacksResponse,
} from "./api";

export function useClickdealerStatus(): UseQueryResult<ClickdealerStatusResponse, Error> {
	return useQuery({
		queryKey: clickdealerIntegrationViaApiQueryKeys.status(),
		queryFn: getStatus,
	});
}

export function useClickdealerCredentialsList(): UseQueryResult<ListCredentialsResponse, Error> {
	return useQuery({
		queryKey: clickdealerIntegrationViaApiQueryKeys.credentials(),
		queryFn: listCredentials,
	});
}

export function useClickdealerOffersList(): UseQueryResult<ListOffersResponse, Error> {
	return useQuery({
		queryKey: clickdealerIntegrationViaApiQueryKeys.offers(),
		queryFn: listOffers,
	});
}

export function useClickdealerPostbacksList(): UseQueryResult<ListPostbacksResponse, Error> {
	return useQuery({
		queryKey: clickdealerIntegrationViaApiQueryKeys.postbacks(),
		queryFn: listPostbacks,
	});
}

export function useClickdealerCredentialsCreate(): UseMutationResult<
	{ ok: boolean; item: ClickdealerCredentialsRow },
	Error,
	CreateCredentialsBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createCredentials,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: clickdealerIntegrationViaApiQueryKeys.all });
		},
	});
}

export function useClickdealerOfferCreate(): UseMutationResult<
	{ ok: boolean; item: ClickdealerOfferRow },
	Error,
	CreateOfferBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createOffer,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: clickdealerIntegrationViaApiQueryKeys.all });
		},
	});
}
